import { StandardStatus, TransactionType } from './property';

/**
 * The single place domain status maps to a semantic theme role (design.md §5.1).
 *
 * Everything that shows state — listing status, transaction type, MLS readiness,
 * selling phase, task priority — resolves here, so a colour is never chosen at
 * the point of use. `StatusChip` turns a role into the tinted chip described in
 * design.md §5.4: alpha(role.main, 0.16) background with role.dark text.
 *
 * `role` is a MUI palette role: 'primary' | 'secondary' | 'success' | 'info' |
 * 'warning' | 'error' | 'default' (neutral slate).
 */

/** Listing lifecycle. Sold is a completed sale, so it reads as brand, not error. */
export const LISTING_STATUS_ROLE = {
  [StandardStatus.ACTIVE]: 'success',
  [StandardStatus.MLS_READY]: 'info',
  [StandardStatus.SOLD]: 'primary',
  [StandardStatus.TERMINATED]: 'error',
  [StandardStatus.EXPIRED]: 'warning',
  [StandardStatus.DRAFT]: 'default',
};

/**
 * Buy vs lease. Informational, never an alert. Covers both vocabularies: the
 * MLS feed uses "For Sale" / "For Lease", user-created properties use
 * Buy / Sell / Rent_in / Rent_out.
 */
export const TRANSACTION_TYPE_ROLE = {
  [TransactionType.FOR_SALE]: 'success',
  [TransactionType.FOR_LEASE]: 'info',
  [TransactionType.FOR_SUB_LEASE]: 'info',
  Sell: 'success',
  Buy: 'info',
  Rent_out: 'warning',
  Rent_in: 'warning',
};

/** Reader-facing label for the user-property transaction vocabulary. */
export const TRANSACTION_TYPE_LABEL = {
  Buy: 'Buy',
  Sell: 'Sell',
  Rent_out: 'Rent Out',
  Rent_in: 'Rent In',
};

/** How close a listing is to being publishable on MLS. */
export const MLS_READINESS_ROLE = {
  ready: 'success',
  almost: 'info',
  incomplete: 'warning',
  blocked: 'error',
};

/** MLS membership claim verification (models/mls_integration.py on the backend). */
export const MLS_CLAIM_STATUS_ROLE = {
  verified: 'success',
  pending: 'warning',
  rejected: 'error',
  unverified: 'default',
};

export const MLS_CLAIM_STATUS_LABEL = {
  verified: 'Verified',
  pending: 'Pending confirmation',
  rejected: 'Not confirmed',
  unverified: 'Not verified',
};

/** Selling-journey phase state (constants/property_selling_phases.py on the backend). */
export const PHASE_STATUS_ROLE = {
  completed: 'success',
  in_progress: 'info',
  current: 'info',
  upcoming: 'default',
  blocked: 'error',
  skipped: 'default',
};

/** Whether a property's address has been verified (AddressVerifiedChip beside the title). */
export const ADDRESS_VERIFICATION_ROLE = {
  verified: 'success',
  unverified: 'warning',
};

export const ADDRESS_VERIFICATION_LABEL = {
  verified: 'Verified',
  unverified: 'Not verified',
};

/** Task / notification priority. */
export const PRIORITY_ROLE = {
  urgent: 'error',
  high: 'warning',
  medium: 'info',
  low: 'default',
};

/**
 * Explicit colours for the two states a reader has to tell apart at a glance.
 * Six listing statuses need six distinguishable colours, which the five semantic
 * roles cannot supply, so these keep the palette the app has always used. They
 * live here, once, rather than being repeated at each call site.
 */
export const LISTING_STATUS_PALETTE = {
  [StandardStatus.ACTIVE]: { bgcolor: '#dcfce7', color: '#15803d' },
  [StandardStatus.MLS_READY]: { bgcolor: '#dbeafe', color: '#1d4ed8' },
  [StandardStatus.SOLD]: { bgcolor: '#fecaca', color: '#dc2626' },
  [StandardStatus.TERMINATED]: { bgcolor: '#fee2e2', color: '#991b1b' },
  [StandardStatus.EXPIRED]: { bgcolor: '#fef3c7', color: '#a16207' },
  [StandardStatus.DRAFT]: { bgcolor: '#fff3cd', color: '#475569' },
};

export const TRANSACTION_TYPE_PALETTE = {
  [TransactionType.FOR_SALE]: { bgcolor: '#f3e5f5', color: '#6a1b9a' },
  [TransactionType.FOR_LEASE]: { bgcolor: '#fff3e0', color: '#e65100' },
  [TransactionType.FOR_SUB_LEASE]: { bgcolor: '#fff3e0', color: '#e65100' },
  Sell: { bgcolor: '#f3e5f5', color: '#6a1b9a' },
  Buy: { bgcolor: '#e3f2fd', color: '#1565c0' },
  Rent_out: { bgcolor: '#fff3e0', color: '#e65100' },
  Rent_in: { bgcolor: '#e8f5e9', color: '#2e7d32' },
};

const PALETTE_MAPS = {
  listing: LISTING_STATUS_PALETTE,
  transaction: TRANSACTION_TYPE_PALETTE,
};

/**
 * The named colour for a value, or null when the kind has no explicit palette
 * and the caller should fall back to a semantic role.
 */
export const getStatusPalette = (value, kind = 'listing') => {
  if (!value) return null;
  const map = PALETTE_MAPS[kind];
  return map ? map[value] || null : null;
};

const ROLE_MAPS = {
  listing: LISTING_STATUS_ROLE,
  transaction: TRANSACTION_TYPE_ROLE,
  mls: MLS_READINESS_ROLE,
  mlsClaim: MLS_CLAIM_STATUS_ROLE,
  phase: PHASE_STATUS_ROLE,
  priority: PRIORITY_ROLE,
  address: ADDRESS_VERIFICATION_ROLE,
};

/**
 * Resolve a domain value to a semantic role.
 * @param {string} value  e.g. 'Active', 'For Sale', 'urgent'
 * @param {'listing'|'transaction'|'mls'|'mlsClaim'|'phase'|'priority'|'address'} kind
 * @returns {string} a palette role, or 'default' when unknown
 */
export const getStatusRole = (value, kind = 'listing') => {
  if (!value) return 'default';
  const map = ROLE_MAPS[kind] || LISTING_STATUS_ROLE;
  return map[value] || map[String(value).toLowerCase()] || 'default';
};

export default getStatusRole;
